import {
  App, Button, Col, Form, Input, Modal, Row, Select, Typography,
} from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { captureException } from 'utils/errors';
import http, { getHttpErrorMessage } from 'utils/http';
import { EventEmitter } from 'utils/event';
import { INGREDIENT_CATEGORY_OPTIONS, INGREDIENT_UNIT_OPTIONS } from './constants';

function IngredientDrawer({
  open,
  ingredient,
  organizationId,
  onClose,
}) {
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const [submitting, setSubmitting] = useState(false);
  const isEditMode = Boolean(ingredient?.id);

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        name: ingredient?.name || '',
        category: ingredient?.category || undefined,
        unit: ingredient?.unit || undefined,
        cost_per_unit: ingredient?.cost_per_unit != null ? String(ingredient?.cost_per_unit) : '',
      });
      return;
    }
    form.resetFields();
  }, [form, ingredient, open]);

  const title = useMemo(
    () => `${isEditMode ? 'Edit' : 'New'} ingredient`,
    [isEditMode],
  );

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const payload = {
        name: values.name?.trim(),
        category: values.category || 'uncategorized',
        unit: values.unit,
        cost_per_unit: values.cost_per_unit ? values.cost_per_unit.trim() : null,
      };
      if (isEditMode) {
        await http.patch(`v1/organizations/${organizationId}/ingredients/${ingredient.id}/`, payload);
        message.success('Ingredient updated successfully');
      } else {
        await http.post(`v1/organizations/${organizationId}/ingredients/`, payload);
        message.success('Ingredient created successfully');
      }
      EventEmitter.emit('refresh_ingredients');
      onClose();
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      captureException(error);
      message.error(getHttpErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={(
        <div>
          <Typography.Title level={5} style={{ margin: 0 }}>
            {title}
          </Typography.Title>
          {!isEditMode && (
            <Typography.Text type="secondary">
              Add a raw material to your library.
            </Typography.Text>
          )}
        </div>
      )}
      open={open}
      onCancel={onClose}
      footer={(
        <Row justify="end" gutter={8} style={{ width: '100%' }}>
          <Col>
            <Button onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
          </Col>
          <Col>
            <Button type="primary" onClick={handleSubmit} loading={submitting}>
              {isEditMode ? 'Save changes' : 'Add ingredient'}
            </Button>
          </Col>
        </Row>
      )}
      width={560}
      centered
      styles={{
        body: { background: '#fff', padding: '20px 24px 12px' },
        header: { borderBottom: '1px solid #f0f0f0', padding: '14px 24px 12px' },
        footer: { borderTop: '1px solid #f0f0f0', padding: '12px 24px 14px' },
      }}
      destroyOnClose
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          label={<Typography.Text strong>Name</Typography.Text>}
          name="name"
          rules={[{ required: true, message: 'Ingredient name is required' }]}
        >
          <Input placeholder="e.g. Roasted red peppers" maxLength={200} />
        </Form.Item>

        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Typography.Text strong>Unit of Measure</Typography.Text>}
              name="unit"
              rules={[{ required: true, message: 'Unit of measurement is required' }]}
            >
              <Select
                showSearch
                options={INGREDIENT_UNIT_OPTIONS}
                optionFilterProp="label"
                placeholder="Select unit"
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item label={<Typography.Text strong>Category (Optional)</Typography.Text>} name="category">
              <Select
                allowClear
                showSearch
                options={INGREDIENT_CATEGORY_OPTIONS}
                optionFilterProp="label"
                placeholder="-- No category --"
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label={<Typography.Text strong>Cost per Unit (Optional)</Typography.Text>}
          name="cost_per_unit"
          rules={[
            {
              validator: (_, value) => {
                if (!value) {
                  return Promise.resolve();
                }
                return /^\d+(\.\d{1,2})?$/.test(value.trim())
                  ? Promise.resolve()
                  : Promise.reject(new Error('Enter a valid amount (up to 2 decimals)'));
              },
            },
          ]}
        >
          <Input
            prefix="$"
            placeholder="0"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default IngredientDrawer;
